import {
    AuthenticationRequest,
    AuthenticationResponse,
    AuthenticatorRegistrationAssertion,
    AuthenticatorSignAssertion,
    CorsKeychain,
    Keychain,
    KeychainReference,
    RegistrationRequest,
    RegistrationResponse
} from '@velorum/browser-authenticator';

import { Injectable, inject } from '@angular/core';
import { from, Observable, of, ReplaySubject } from 'rxjs';
import { catchError, map, mergeMap, switchMap, take } from 'rxjs/operators';

import { AuthenticationToken, RegistrationStage } from './authentication.model';
import { ApVelorumInterface } from './velorum.interface';

@Injectable({ providedIn: 'root' })
export class ApAuthenticationService {
    private readonly velorumInterface = inject(ApVelorumInterface);

    /**
     * Get token
     * @param id
     * @returns {Observable<AuthenticationToken>}
     */
    public getToken(id: string): Observable<AuthenticationToken> {
        return from(this.listKeychains()).pipe(
            map((references: KeychainReference[]) => references.find((reference) => reference.id === id)),
            map((reference: KeychainReference) =>
                reference
                    ? {
                          description: reference.data && reference.data.browserAlias,
                          keychainId: reference.id,
                          username: reference.data && reference.data.username,
                          stage: reference.data && reference.data.stage
                      }
                    : null
            )
        );
    }

    public deleteToken(keychainId: string): Observable<void> {
        return from(this.velorumInterface.deleteKeychain(keychainId));
    }

    public generateToken(browserAlias: string, username: string): Keychain {
        const keychain = this.velorumInterface.newKeychain();
        keychain.setData({ browserAlias, username, stage: RegistrationStage.CREATED });
        return keychain;
    }

    public storeToken(keychain: Keychain): Observable<void> {
        return keychain ? from(this.velorumInterface.storeKeychain(keychain)).pipe(map(() => null)) : of(null);
    }

    public setStage(keychainId: string, stage: RegistrationStage): Observable<void> {
        return from(this.velorumInterface.getKeychain(keychainId)).pipe(
            switchMap((keychain) => {
                keychain.setData({ ...keychain.getData(), stage });
                return this.storeToken(keychain);
            })
        );
    }

    public async generateRegistrationResponse(
        keychain: Keychain,
        registrationRequest: RegistrationRequest,
        pincode: string
    ): Promise<RegistrationResponse> {
        const assertions = await this.getRegistrationAssertions(keychain, registrationRequest, pincode);
        return this.velorumInterface.generateRegistrationResponse(registrationRequest, assertions);
    }

    public async generateAuthenticationResponse(
        keychainId: string,
        authenticationRequest: AuthenticationRequest,
        pincode?: string
    ): Promise<AuthenticationResponse> {
        const keychain = await this.getKeychain(keychainId);
        const assertions = await this.getAuthenticationAssertions(keychain, authenticationRequest, pincode);
        return this.velorumInterface.generateAuthenticationResponse(authenticationRequest, assertions);
    }

    public getTokenIds(): Observable<string[]> {
        return from(this.listKeychains()).pipe(map((references) => references.map((reference) => reference.id)));
    }

    public getMatchingKeychainIds(browserKeyIds: string[], pincodeKeyIds: string[]): Observable<string[]> {
        return this.getTokenIds().pipe(
            mergeMap((tokenIds: string[]) => Promise.all(tokenIds.map((tokenId: string) => this.getKeychain(tokenId)))),
            mergeMap((keychains) =>
                Promise.all(
                    keychains.map(async (keychain) => ({
                        kc: keychain,
                        bbk: await keychain.getBrowserBindingKey(),
                        pk: await keychain.getPasswordKey()
                    }))
                )
            ),
            map((keychains) =>
                keychains.filter(
                    (keychain) =>
                        browserKeyIds.indexOf(keychain.bbk.getEncodedKeyId()) !== -1 &&
                        pincodeKeyIds.indexOf(keychain.pk.getEncodedKeyId()) !== -1
                )
            ),
            map((keychains) => keychains.map((keychain) => keychain.kc['id']))
        );
    }

    public initCors(targetOrigin: string, targetPath: string, timeout: number): Observable<void> {
        return this.isCorsReady()
            ? of(null)
            : from(this.velorumInterface.initCorsKeychain(targetOrigin, targetPath, timeout)).pipe(catchError(() => of(null)));
    }

    public getBrowserBindingKey(keychainId: string): Observable<string> {
        return from(this.velorumInterface.getKeychain(keychainId)).pipe(
            map((keychain) => keychain.getBrowserBindingKey().getEncodedKeyId())
        );
    }

    public isBrowserCompatible(): Observable<boolean> {
        const databaseName = 'compat-test';
        const result = new ReplaySubject<boolean>();
        try {
            if (!window.indexedDB) {
                result.next(false);
            } else {
                const openRequest = window.indexedDB.open(databaseName, 1);
                openRequest.onsuccess = () => {
                    openRequest.result.close();

                    const deleteRequest = window.indexedDB.deleteDatabase(databaseName);
                    deleteRequest.onsuccess = () => {
                        result.next(true);
                    };
                    deleteRequest.onerror = () => {
                        result.next(false);
                    };
                };
                openRequest.onerror = () => {
                    result.next(false);
                };
            }
        } catch (e) {
            // in Firefox setting dom.indexedDB.enabled to false
            // will throw an error when window.indexedDB is read
            result.next(false);
        }
        return result.pipe(take(1));
    }

    public getFingerprint(): string {
        try {
            return btoa(String.fromCharCode.apply(null, this.velorumInterface.getFingerprint()));
        } catch (e) {
            return null;
        }
    }

    private async getRegistrationAssertions(
        keychain: Keychain,
        registrationRequest: RegistrationRequest,
        pincode: string
    ): Promise<AuthenticatorRegistrationAssertion[]> {
        const browserBindingKey = this.velorumInterface.newBrowserBindingKey();
        const browserBindingAuthenticator = this.velorumInterface.newBrowserBindingAuthenticator(browserBindingKey);
        const browserBindingAssertion: Promise<AuthenticatorRegistrationAssertion> = browserBindingAuthenticator.getRegistrationAssertion(
            registrationRequest,
            null
        );
        keychain.setBrowserBindingKey(browserBindingKey);

        const passwordKey = this.velorumInterface.newPasswordKey();
        const passwordAuthenticator = this.velorumInterface.newPasswordAuthenticator(passwordKey);
        const passwordAssertion: Promise<AuthenticatorRegistrationAssertion> = passwordAuthenticator.getRegistrationAssertion(
            registrationRequest,
            pincode
        );
        keychain.setPasswordKey(passwordKey);

        return Promise.all([browserBindingAssertion, passwordAssertion]);
    }

    private async getAuthenticationAssertions(
        keychain: Keychain | CorsKeychain,
        authenticationRequest: AuthenticationRequest,
        pincode?: string
    ): Promise<AuthenticatorSignAssertion[]> {
        const assertions = [];
        const browserBindingAuthenticator = this.velorumInterface.newBrowserBindingAuthenticator(await keychain.getBrowserBindingKey());
        const browserBindingAssertion: Promise<AuthenticatorSignAssertion> = browserBindingAuthenticator.getAuthenticationAssertion(
            authenticationRequest,
            null
        );
        assertions.push(browserBindingAssertion);

        if (pincode !== undefined) {
            const passwordAuthenticator = this.velorumInterface.newPasswordAuthenticator(await keychain.getPasswordKey());
            const passwordAssertion: Promise<AuthenticatorSignAssertion> = passwordAuthenticator.getAuthenticationAssertion(
                authenticationRequest,
                pincode
            );
            assertions.push(passwordAssertion);
        }
        return Promise.all(assertions);
    }

    private async listKeychains(): Promise<KeychainReference[]> {
        try {
            return this.isCorsReady() ? await this.velorumInterface.listCorsKeychains() : await this.velorumInterface.listKeychains();
        } catch (e) {
            console.error(e);
            return [];
        }
    }

    private async getKeychain(keychainId: string): Promise<Keychain | CorsKeychain> {
        return this.isCorsReady() ? this.velorumInterface.getCorsKeychain(keychainId) : this.velorumInterface.getKeychain(keychainId);
    }

    private isCorsReady(): boolean {
        try {
            return this.velorumInterface.isCorsReady();
        } catch (e) {
            return false;
        }
    }
}
